import React from "react";
import { ReactNode } from "react";
import ObservableState from "../utils/ObservableState";
import * as ui from "../native";
import ObservableComponent from "./ObservableComponent";
import BaseUIProps, { copyBaseUIProps } from "../native/ui/BaseUIProps";
import ObjectObservable from "../utils/ObjectObservable";
import Button from "./Button";
import DBSaveStatus from "../utils/DBSaveStatus";
import LabelWithInputField from "./LabelWithInputField";
import TaxRate from "../models/TaxRate";
import ResultStatus from "../classes/ResultStatus";
import LabelWithDoubleInputField from "./LabelWithDoubleInputField";
import TextView from "./TextView";
import PageNavigator from "../classes/PageNavigator";
import LabelDropdown from "./LabelDropdown";
import Result from "../classes/Result";
import Divider from "./Divider";
import { Runnable } from "../classes/core";
import { BuildContext } from "../classes/BuildContext";

type _CancelOnPressed = (d3eState: AdminAddTaxesViewRefs) => void;

type _SaveButtonOnPressed = (d3eState: AdminAddTaxesViewRefs) => void;

type _LabelDropdownOnChanged = (
  text: string,
  d3eState: AdminAddTaxesViewRefs
) => void;

type _DescriptionFieldOnChanged = (
  text: string,
  d3eState: AdminAddTaxesViewRefs
) => void;

type _ObjFieldOnChanged = (
  text: number,
  d3eState: AdminAddTaxesViewRefs
) => void;

export interface AdminAddTaxesViewProps extends BaseUIProps {
  key?: string;
  obj: TaxRate;
}
/// To store state data for AdminAddTaxesView
class AdminAddTaxesViewRefs {
  public cancel: CancelState = new CancelState();
  public saveButton: SaveButtonState = new SaveButtonState();
}

interface SaveButtonWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminAddTaxesViewRefs;
  _onCreateButtonClicked?: _SaveButtonOnPressed;
}

class SaveButtonState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _SaveButtonWithState extends ObservableComponent<SaveButtonWithStateProps> {
  saveButtonFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: SaveButtonWithStateProps) {
    super(props);

    this.initState();
  }
  public get saveButton(): SaveButtonState {
    return this.props.d3eState.saveButton;
  }
  public get d3eState(): AdminAddTaxesViewRefs {
    return this.props.d3eState;
  }
  public get _onCreateButtonClicked(): _SaveButtonOnPressed {
    return this.props._onCreateButtonClicked;
  }
  public initState() {
    super.initState();

    this.updateObservable("saveButton", null, this.saveButton);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["saveButton", "saveButton.", "saveButton.hover"], this.rebuild);
  }
  public saveButtonOnEnter(event): void {
    return this.saveButton.setHover(true);
  }
  public saveButtonOnExit(event): void {
    return this.saveButton.setHover(false);
  }
  public dispose(): void {
    this.saveButton.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: this.saveButton.hover
          ? cStyle.tButtonPrimaryPaddingOnHover
          : cStyle.tButtonPrimaryPaddingOn,
        decoration: this.saveButton.hover
          ? cStyle.tButtonPrimaryDecorationOnHover
          : cStyle.tButtonPrimaryDecorationOn,
        disable: this.saveButton.disable,
        onPressed: () => {
          this._onCreateButtonClicked(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Save" }),
        onEnter: (event) => {
          this.saveButtonOnEnter(event);
        },
        onExit: (event) => {
          this.saveButtonOnExit(event);
        },
      }),
      className: "x3e",
    });
  }
}
function SaveButtonWithState(props: SaveButtonWithStateProps) {
  return React.createElement(_SaveButtonWithState, props);
}

interface CancelWithStateProps extends BaseUIProps {
  key?: string;
  d3eState: AdminAddTaxesViewRefs;
  _onCancelButtonClicked?: _CancelOnPressed;
}

class CancelState extends ObjectObservable {
  private _disable: boolean = false;
  public _hover: boolean = false;
  public get disable(): boolean {
    return this._disable;
  }
  public setDisable(val: boolean) {
    let isValChanged: boolean = this._disable !== val;

    if (!isValChanged) {
      return;
    }

    this._disable = val;

    this.fire("disable", this);
  }
  public get hover(): boolean {
    return this._hover;
  }
  public setHover(val: boolean) {
    let isValChanged: boolean = this._hover !== val;

    if (!isValChanged) {
      return;
    }

    this._hover = val;

    this.fire("hover", this);
  }
}

class _CancelWithState extends ObservableComponent<CancelWithStateProps> {
  cancelFocusNode: ui.FocusNode = new ui.FocusNode();
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: CancelWithStateProps) {
    super(props);

    this.initState();
  }
  public get cancel(): CancelState {
    return this.props.d3eState.cancel;
  }
  public get d3eState(): AdminAddTaxesViewRefs {
    return this.props.d3eState;
  }
  public get _onCancelButtonClicked(): _CancelOnPressed {
    return this.props._onCancelButtonClicked;
  }
  public initState() {
    super.initState();

    this.updateObservable("cancel", null, this.cancel);

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.on(["cancel", "cancel.", "cancel.hover"], this.rebuild);
  }
  public cancelOnEnter(event): void {
    return this.cancel.setHover(true);
  }
  public cancelOnExit(event): void {
    return this.cancel.setHover(false);
  }
  public dispose(): void {
    this.cancel.setHover(false);

    super.dispose();
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Container({
      child: Button({
        padding: this.cancel.hover
          ? cStyle.tButtonPrimaryOutlinePaddingOnHover
          : cStyle.tButtonPrimaryOutlinePaddingOn,
        decoration: this.cancel.hover
          ? cStyle.tButtonPrimaryOutlineDecorationOnHover
          : cStyle.tButtonPrimaryOutlineDecorationOn,
        disable: this.cancel.disable,
        onPressed: () => {
          this._onCancelButtonClicked(this.d3eState);
        },
        onFocusChange: (val) => {},
        child: TextView({ data: "Cancel" }),
        onEnter: (event) => {
          this.cancelOnEnter(event);
        },
        onExit: (event) => {
          this.cancelOnExit(event);
        },
      }),
      className: "x2c",
    });
  }
}
function CancelWithState(props: CancelWithStateProps) {
  return React.createElement(_CancelWithState, props);
}

class _AdminAddTaxesViewState extends ObservableComponent<AdminAddTaxesViewProps> {
  static defaultProps = { obj: null };
  d3eState: AdminAddTaxesViewRefs = new AdminAddTaxesViewRefs();
  isSavedObj: boolean = false;
  private _editInitListeners: Array<Runnable> = [];
  private _d3eModelErrors: Array<string> = [];
  static contextType = BuildContext;
  context: React.ContextType<typeof BuildContext>;
  public constructor(props: AdminAddTaxesViewProps) {
    super(props);

    this.initState();
  }
  public get obj(): TaxRate {
    return this.props.obj;
  }
  public initState() {
    super.initState();

    this.initListeners();

    this.enableBuild = true;
  }
  public initListeners(): void {
    this.updateSyncProperty("obj", this.props.obj);

    this.on(["obj"], this.computeIsSavedObj);

    this.computeIsSavedObj();

    this.on(
      ["isSavedObj", "obj", "obj.country", "obj.jurisdiction", "obj.rate"],
      this.rebuild
    );
  }
  public componentDidUpdate(prevProps: AdminAddTaxesViewProps): void {
    super.componentDidUpdate(prevProps);

    if (prevProps.obj !== this.props.obj) {
      this.updateObservable("obj", prevProps.obj, this.props.obj);

      this.fire("obj", this);
    }
  }
  public setIsSavedObj(val: boolean): void {
    let isValChanged: boolean = this.isSavedObj !== val;

    if (!isValChanged) {
      return;
    }

    this.isSavedObj = val;

    this.fire("isSavedObj", this);
  }
  public computeIsSavedObj = (): void => {
    try {
      this.setIsSavedObj(this.obj.saveStatus !== DBSaveStatus.New);
    } catch (exception) {
      console.log(" exception in computeIsSavedObj : " + exception.toString());

      this.setIsSavedObj(false);
    }
  };
  public get d3eModelErrors(): Array<string> {
    return this._d3eModelErrors ?? [];
  }
  public set d3eModelErrors(val: Array<string>) {
    this._d3eModelErrors.clear();

    this._d3eModelErrors.addAll(val);
  }
  public setEditDefaults(): void {}
  public get isEditValid(): boolean {
    return true;
  }
  public validate(): Array<string> {
    let errors: Array<string> = [];

    return errors;
  }
  public render(): ReactNode {
    let cStyle = this.context.theme;

    return ui.Column({
      crossAxisAlignment: ui.CrossAxisAlignment.start,
      children: [
        TextView({
          data: this.isSavedObj ? "Edit Tax Rate" : "Add Tax Rate",
          style: new ui.TextStyle({
            fontSize: 20,
            fontWeight: cStyle.tTextViewHeadlineFourFontWeightOn,
          }),
          className: "x48 hc",
          key: "0",
        }),
        Divider({ className: "xf6 hc h", key: "1" }),
        LabelDropdown<string>({
          name: "Country",
          placeHolder: "Select Country",
          value: this.obj.country,
          items: ["India", "USA", "UK", "Australia", "Canada"],
          onChanged: (text) => {
            this.labelDropdownonChanged(text, this.d3eState);
          },
          builder: (context, item) => {
            return TextView({ data: item });
          },
          className: "x24 hc h",
          key: "2",
        }),
        LabelWithInputField({
          name: "Jurisdiction",
          placeHolder: "Enter Jurisdiction",
          value: this.obj.jurisdiction,
          onChanged: (text) => {
            this.descriptionFieldonChanged(text, this.d3eState);
          },
          className: "xaa hc h",
          key: "3",
        }),
        LabelWithDoubleInputField({
          name: "Percentage",
          placeHolder: "Enter  Percentage",
          value: this.obj.rate,
          onChanged: (text) => {
            this.objFieldonChanged(text, this.d3eState);
          },
          className: "x309 hc h",
          key: "4",
        }),
        ui.Row({
          mainAxisAlignment: ui.MainAxisAlignment.spaceBetween,
          children: [
            CancelWithState({
              d3eState: this.d3eState,
              _onCancelButtonClicked: this.onCancelButtonClicked,
              key: "0",
            }),
            SaveButtonWithState({
              d3eState: this.d3eState,
              _onCreateButtonClicked: this.onCreateButtonClicked,
              key: "1",
            }),
          ],
          className: "x0b hc h",
          key: "5",
        }),
      ],
      className: ui.join(this.props.className, "AdminAddTaxesView x42 hc vc"),
      ...copyBaseUIProps(this.props),
    });
  }
  public onCreateButtonClicked = async (
    d3eState: AdminAddTaxesViewRefs
  ): Promise<void> => {
    let result: Result<TaxRate> = await this.obj.save();

    if (result.status === ResultStatus.Success) {
      this.navigator.close();
    }
  };
  public onCancelButtonClicked = (d3eState: AdminAddTaxesViewRefs): void => {
    this.navigator.close();
  };
  public labelDropdownonChanged = (
    val: string,
    d3eState: AdminAddTaxesViewRefs
  ): void => {
    this.obj.setCountry(val);
  };
  public descriptionFieldonChanged = (
    val: string,
    d3eState: AdminAddTaxesViewRefs
  ): void => {
    this.obj.setJurisdiction(val);
  };
  public objFieldonChanged = (
    val: number,
    d3eState: AdminAddTaxesViewRefs
  ): void => {
    this.obj.setRate(val);
  };
  public get navigator(): PageNavigator {
    return PageNavigator.of(this.context);
  }
  public get cancel() {
    return this.d3eState.cancel;
  }
  public get saveButton() {
    return this.d3eState.saveButton;
  }
}
export default function AdminAddTaxesView(props: AdminAddTaxesViewProps) {
  return React.createElement(_AdminAddTaxesViewState, {
    ..._AdminAddTaxesViewState.defaultProps,
    ...props,
  });
}
